import { Typography } from '@mui/material';
import { Checkbox, Option } from 'components/ui/FormControls';
import React, { ChangeEvent, useCallback, useEffect } from 'react';

import {
    useFormControl,
    useFormGroup,
    useManageTemplateForm,
} from '../../../../utils/form.utils';
import ManageTemplate, {
    ManageTemplateInstance,
} from '../../../ui/ManageTemplate';
import { ChannelSubscriptionType } from '../../../../shared/types';
import ManageTemplateFormItem from '../../../ui/ManageTemplateFormItem';
import {
    getPremiumChannelsSubscribersFormBreadCrumbs,
    getPremiumChannelsSubscribersFormCreateButtonLabel,
    getPremiumChannelsSubscribersFormInstance,
    premiumChannelsSubscribersFormDeleteButtonLabel,
    premiumChannelsSubscribersFormDeleteDialogTitle,
    PremiumChannelsSubscribersFormInterface,
    premiumChannelsSubscribersFormUpdateButtonLabel,
    helperTextSx,
} from './ChannelSubscribersShared';
import {
    CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY,
    CREATE_FREE_CHANNEL_SUBSCRIPTION,
    UPDATE_FREE_CHANNEL_SUBSCRIPTION,
} from '../../../../gql/subscriptions';

export const FreeChannelSubscribersForm = (
    props: PremiumChannelsSubscribersFormInterface
) => {
    const {
        channelId,
        userFormControl,
        notesFormControl,
        fileFormControl,
        returnUrl,
        isEdit,
        isBulk,
        channel,
        proceedBulk,
        usersField,
        notesField,
        currentUser,
        onDelete,
    } = props;

    const isFullArticleContentControl = useFormControl<boolean>(false, []);

    useEffect(() => {
        if (isEdit) {
            const cs = currentUser?.channelSubscriptions[0];
            if (!cs) return;

            currentUser &&
                userFormControl.setValue({
                    label: `${currentUser.name} (${currentUser.email})`,
                    value: currentUser._id,
                } as Option);

            notesFormControl.setValue(cs.notes);
            isFullArticleContentControl.setValue(
                cs.isFullArticleContent || false
            );
        }
    }, [isEdit, channel]);

    const addSubscriberEndpoint = () => {
        return CREATE_FREE_CHANNEL_SUBSCRIPTION;
    };

    const editSubscriberEndpoint = () => {
        return UPDATE_FREE_CHANNEL_SUBSCRIPTION;
    };

    const deleteSubscriberEndpoint = () => {
        return CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY;
    };

    const form = useFormGroup({
        ...(isBulk
            ? {
                  file: fileFormControl,
              }
            : {
                  user: userFormControl,
              }),
        notes: notesFormControl,
        isFullArticleContent: isFullArticleContentControl,
    });

    const {
        isNew,
        formMessage,
        formBusy,
        onSubmit,
        onCancel,
        onAlertBoxClose,
    } = useManageTemplateForm(
        ManageTemplateInstance.ChannelSubscriber,
        form,
        isEdit,
        addSubscriberEndpoint(),
        editSubscriberEndpoint(),
        deleteSubscriberEndpoint(),
        returnUrl
    );

    const getReqBody = useCallback(() => {
        const { value } = form;

        return {
            channelId,
            ...(value.user ? { userId: value.user } : {}),
            notes: value.notes,
            subscriptionType: ChannelSubscriptionType.Free,
            isFullArticleContent: isFullArticleContentControl.value,
            addedByAdmin: !isBulk ? true : undefined,
        };
    }, [form]);

    const onBulkProceed = () => {
        form.setDirty();
        if (!form.isValid) return;
        proceedBulk(getReqBody());
    };

    return (
        <ManageTemplate
            pathArea={'Channels'}
            returnUrl={returnUrl}
            templateInstance={getPremiumChannelsSubscribersFormInstance(isBulk)}
            isNew={isNew}
            formMessage={formMessage}
            formBusy={formBusy}
            onCancel={onCancel}
            onSubmit={(ev: any): void => {
                !isBulk ? onSubmit(ev, getReqBody()) : onBulkProceed();
            }}
            onDelete={onDelete}
            onAlertBoxClose={onAlertBoxClose}
            templateCreateButtonLabel={getPremiumChannelsSubscribersFormCreateButtonLabel(
                isBulk
            )}
            templateUpdateButtonLabel={
                premiumChannelsSubscribersFormUpdateButtonLabel
            }
            templateDeleteButtonLabel={
                premiumChannelsSubscribersFormDeleteButtonLabel
            }
            templateDeleteDialogTitle={
                premiumChannelsSubscribersFormDeleteDialogTitle
            }
            breadcrumbOverride={getPremiumChannelsSubscribersFormBreadCrumbs(
                channel.name,
                returnUrl,
                isEdit,
                isBulk
            )}
            maxContentWidth={isEdit ? 450 : 420}
            helperContent={
                <Typography sx={helperTextSx}>
                    Manually add a user as active subscriber of your channel.
                </Typography>
            }
        >
            {usersField}
            {notesField}
            <ManageTemplateFormItem>
                <Checkbox
                    id="fullArticleContent-checkbox"
                    label="Send full article content in email notifications"
                    checked={isFullArticleContentControl?.value}
                    value="a"
                    onChange={(e: ChangeEvent<any>): void => {
                        isFullArticleContentControl?.setValue(
                            e?.target?.checked
                        );
                    }}
                />
            </ManageTemplateFormItem>
        </ManageTemplate>
    );
};
